import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Handlebars from "handlebars";
import dayjs from "dayjs";
import { sendToWindowDataLayer } from "../utils";
import { updateAppointment } from "../appointments/appointmentSlice";
import {
  getBookingTimes,
  checkDayAvailability,
} from "../functions/getBookingTimes";
import { NextBackButtons } from "components";

import {
  dayRow,
  buttonPrimary,
  buttonSecondary,
  cardHeaderClass,
  cardBodyClass,
  textArea,
} from "css";

const AppointmentPicker = ({ appointment }) => {
  const [stayOrDropOff, setStayOrDropOff] = useState(
    appointment?.slot?.dropOff || ""
  );
  const [selectedDate, setSelectedDate] = useState(
    appointment?.slot?.date || ""
  );
  const [selectedTime, setSelectedTime] = useState(
    appointment?.slot?.time || ""
  );
  const [complimentaryRide, setComplimentaryRide] = useState("");
  const [noSlotsMessage, setNoSlotsMessage] = useState(
    "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly."
  );
  const [startDate, setStartDate] = useState(new Date());
  const [bounceComplimentaryRide, setBounceComplimentaryRide] = useState(false);
  const [bookingTimes, setBookingTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clear, setClear] = useState(false);
  const [findingAvailableDay, setFindingAvailableDay] = useState(false);
  const [dayAvailability, setDayAvailability] = useState({});

  const dispatch = useDispatch();

  useEffect(() => {
    buildHandlebars();
  }, []);

  // Find the first available day when the appointment type is selected
  useEffect(() => {
    if (stayOrDropOff && !selectedDate && !findingAvailableDay) {
      findFirstAvailableDay();
    }
  }, [stayOrDropOff]);

  const buildHandlebars = () => {
    const template = Handlebars.compile(
      appointment?.shopData?.messages?.noSlots ??
        "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly."
    );

    const data = {
      your_service: appointment?.service,
      on_date: appointment?.date,
      at_time: appointment?.time,
    };
    const result = template(data);
    setNoSlotsMessage(result);
  };

  let sameDayBookings =
    appointment?.shopData?.sameDayBookings !== undefined
      ? appointment?.shopData?.sameDayBookings
      : false;
  let defaultSchedule = appointment?.shopData?.shopSchedule || [];

  const findFirstAvailableDay = async () => {
    setFindingAvailableDay(true);
    const availabilityMap = { ...dayAvailability };
    let currentStartDate = new Date(startDate);
    let foundAvailableDay = false;
    let iterationCount = 0;

    while (!foundAvailableDay && iterationCount < 4) {
      // Check up to 28 days (4 weeks)
      const daysToCheck = getNext7Days(
        currentStartDate,
        defaultSchedule,
        sameDayBookings
      );

      for (const day of daysToCheck) {
        const dateStr = dayjs(day).format("YYYY-MM-DD");
        if (availabilityMap[dateStr] === undefined) {
          const result = await checkDayAvailability(day, appointment);
          availabilityMap[dateStr] = result.hasAvailableSlots;

          if (result.hasAvailableSlots) {
            dispatch(
              updateAppointment({
                updates: {
                  date: dayjs(day).format("MM/DD/YYYY"),
                },
              })
            );

            // If the available day is beyond the current visible range, update startDate
            if (dayjs(day).isAfter(dayjs(startDate).add(6, "day"))) {
              let newStartDate = new Date(startDate);
              while (dayjs(newStartDate).add(6, "day").isBefore(dayjs(day))) {
                newStartDate.setDate(newStartDate.getDate() + 7);
              }
              setStartDate(newStartDate);
            }

            setSelectedDate(day);
            setDayAvailability(availabilityMap);
            setFindingAvailableDay(false);
            foundAvailableDay = true;
            return;
          }
        } else if (availabilityMap[dateStr] === true) {
          dispatch(
            updateAppointment({
              updates: {
                date: dayjs(day).format("MM/DD/YYYY"),
              },
            })
          );

          // If the available day is beyond the current visible range, update startDate
          if (dayjs(day).isAfter(dayjs(startDate).add(6, "day"))) {
            let newStartDate = new Date(startDate);
            while (dayjs(newStartDate).add(6, "day").isBefore(dayjs(day))) {
              newStartDate.setDate(newStartDate.getDate() + 7);
            }
            setStartDate(newStartDate);
          }

          setSelectedDate(day);
          setDayAvailability(availabilityMap);
          setFindingAvailableDay(false);
          foundAvailableDay = true;
          return;
        }
      }

      // Move to next week if no available days found
      currentStartDate.setDate(currentStartDate.getDate() + 7);
      iterationCount++;
    }

    setDayAvailability(availabilityMap);
    setFindingAvailableDay(false);
  };

  const fetchSlotTimes = async () => {
    let tmdate = dayjs(selectedDate).format("YYYY-MM-DD");
    // let config = {
    //   domain: appointment?.domain,
    //   shopId: appointment?.shopData?.shopId,
    // };

    let adjustForBuffer;
    const response = await getBookingTimes(tmdate, appointment);
    if (
      dayjs(selectedDate).format("MM/DD/YYYY") ===
      dayjs(new Date()).format("MM/DD/YYYY")
    ) {
      adjustForBuffer = response.availableSlots.slice(
        appointment?.shopData?.slotBuffer
      );
      setBookingTimes(adjustForBuffer);
    } else {
      setBookingTimes(response.availableSlots);
    }

    document
      .getElementById("time-picker")
      .scrollIntoView({ behavior: "smooth", block: "start" });
    setLoading(false);
  };

  useEffect(() => {
    if (selectedDate) {
      sendToWindowDataLayer(
        "dc-service-date",
        dayjs(selectedDate).format("ddd DD MMM")
      );

      setLoading(true);
      fetchSlotTimes(appointment?.shopData?.shopSchedule);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (stayOrDropOff) {
      if (stayOrDropOff === "wait") {
        sendToWindowDataLayer("dc-service-wait_or_drop", "Stay");
      } else if (stayOrDropOff === "drop") {
        sendToWindowDataLayer("dc-service-wait_or_drop", "Drop Off");
      }

      document
        .getElementById("date-picker")
        .scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stayOrDropOff]);

  useEffect(() => {
    if (selectedTime) {
      sendToWindowDataLayer("dc-service-time", selectedTime);

      if (appointment?.shopData?.complimentaryRide) {
        document
          .getElementById("ride-picker")
          .scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        document
          .getElementById("appointment-summary")
          .scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [selectedTime, complimentaryRide]);

  let hostname = `https://${
    // eslint-disable-next-line no-restricted-globals
    location.hostname.split(".")[0]
  }.api.digitalconcierge.io`;

  if (hostname.includes("localhost")) {
    hostname = "http://localhost:3020";
  }

  const getNext7Days = (fromDate, schedule, sameDayBookings) => {
    const days = [];
    let todayTime = new Date().getTime();
    let fromDateTime = new Date().getTime();
    let i = 0;
    !sameDayBookings && todayTime === fromDateTime && i++;
    while (days.length < 7 && i < 14) {
      // Added a limit to avoid infinite loop in case all days are closed
      const day = new Date(fromDate);
      day.setDate(fromDate.getDate() + i);
      const dayOfWeek = day.getDay();
      const daySchedule = schedule.find((sched) => sched.day === dayOfWeek);

      if (daySchedule && daySchedule.open) {
        days.push(day);
      }
      i++;
    }
    return days;
  };

  const loadNextDates = () => {
    let newStartDate = new Date(startDate);
    let addedDays = 0;
    while (addedDays < 7) {
      newStartDate.setDate(newStartDate.getDate() + 1);
      addedDays++;
    }
    setStartDate(newStartDate);
  };

  const loadPreviousDates = () => {
    let newStartDate = new Date(startDate);
    let subtractedDays = 0;
    let today = new Date();
    today.setHours(0, 0, 0, 0); // Set the time to 00:00:00 to compare only the date part

    while (subtractedDays < 7 && newStartDate > today) {
      newStartDate.setDate(newStartDate.getDate() - 1);
      const dayOfWeek = newStartDate.getDay();
      const daySchedule = appointment?.shopData?.shopSchedule.find(
        (sched) => sched.day === dayOfWeek
      );

      if (daySchedule && daySchedule.open) subtractedDays++;
    }

    // If newStartDate is before today, reset it to today
    if (newStartDate < today) newStartDate = today;

    setStartDate(newStartDate);
  };

  const onCommentChange = (e) => {
    if (e.target.value !== "" || !appointment.shopData?.requireComments) {
      dispatch(updateAppointment({ updates: { comments: e.target.value } }));
      setClear(true);
    } else {
      dispatch(updateAppointment({ updates: { comments: "" } }));
      setClear(false);
    }
  };

  return (
    <>
      <div className="mx-6 pt-6">
        <div
          className={`${cardHeaderClass} flex justify-between`}
          style={{ backgroundColor: appointment?.shopData?.color }}
        >
          Will you be dropping your vehicle off?
          <span className="relative flex h-3 w-3 pt-1.5">
            {stayOrDropOff ? (
              <>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
              </>
            ) : (
              <>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </>
            )}
          </span>
        </div>
        <div
          className={cardBodyClass}
          style={{ borderColor: appointment?.shopData?.color }}
        >
          <div className="grid grid-cols-2 gap-5 p-2">
            {["wait", "drop"].map((choice) => (
              <div
                key={choice}
                dc-service={`dc-service-${choice}`}
                onClick={() => {
                  if (appointment?.dropOffOnly && choice === "drop") {
                    dispatch(
                      updateAppointment({
                        updates: { apptType: choice },
                      })
                    );

                    setSelectedDate(appointment?.slot?.date);
                    setSelectedTime(appointment?.slot?.time);
                    setStayOrDropOff(choice);
                    // setComplimentaryRide(choice);
                  } else if (!appointment?.dropOffOnly) {
                    dispatch(
                      updateAppointment({
                        updates: { apptType: choice },
                      })
                    );
                    setSelectedDate(appointment?.slot?.date);
                    setSelectedTime(appointment?.slot?.time);
                    setStayOrDropOff(choice);
                    // setComplimentaryRide(choice);
                  }
                }}
              >
                <center>
                  <div
                    className={`size-20 md:w-[160px] md:h-[80px] p-2 ${
                      choice === stayOrDropOff ? buttonSecondary : buttonPrimary
                    } ${
                      appointment?.dropOffOnly && choice === "wait"
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      appointment?.dropOffOnly &&
                      choice === "wait" &&
                      "text-gray-300 hover:text-gray-300"
                    }`}
                    style={{
                      backgroundColor:
                        choice === stayOrDropOff
                          ? appointment?.shopData?.color
                          : "white",
                      border: `1px solid ${appointment?.shopData?.color}`,
                      boxShadow:
                        appointment?.shopData?.shadow === "shadow-md"
                          ? `0 4px 6px ${appointment?.shopData?.color}33`
                          : "none",
                    }}
                  >
                    {choice === "wait"
                      ? appointment?.dropOffOnly
                        ? appointment?.shopData?.messages?.dropOffOnlyMessage
                          ? appointment?.shopData?.messages?.dropOffOnlyMessage
                          : "This is a drop off only service"
                        : "Stay"
                      : "Drop Off"}
                  </div>
                </center>
              </div>
            ))}
          </div>
        </div>

        {stayOrDropOff && (
          <>
            <br />

            <div
              id="date-picker"
              className={`${cardHeaderClass} flex justify-between`}
              style={{
                backgroundColor: appointment?.shopData?.color,
              }}
            >
              Select a Date
              <span className="relative flex h-3 w-3 pt-1.5">
                {selectedDate ? (
                  <>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
                  </>
                ) : (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                  </>
                )}
              </span>
            </div>
            <div
              className={cardBodyClass}
              style={{
                borderColor: appointment?.shopData?.color,
              }}
            >
              {findingAvailableDay ? (
                <div className="col-span-3 md:col-span-6">
                  <center>
                    <img
                      src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
                      alt="loading"
                    />
                    <br />
                    <br />
                    Finding the best times for you!
                  </center>
                </div>
              ) : (
                <div className={dayRow}>
                  <div>
                    <div
                      className={`w-9 mr-2 h-[80px] rounded-none cursor-pointer ${buttonPrimary}`}
                      onClick={loadPreviousDates}
                      style={{
                        border: `1px solid ${appointment?.shopData?.color}`,
                        boxShadow:
                          appointment?.shopData?.shadow === "shadow-md"
                            ? `0 4px 6px ${appointment?.shopData?.color}33`
                            : "none",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 19.5 8.25 12l7.5-7.5"
                        />
                      </svg>
                    </div>
                  </div>

                  {getNext7Days(
                    startDate,
                    defaultSchedule,
                    sameDayBookings
                  ).map((date, index) => (
                    <div
                      key={index}
                      service-date={date}
                      className={`flex justify-center text-center items-center mx-1 mt-2 w-[80px] h-[80px]`}
                    >
                      <div
                        className="w-[80px] cursor-pointer"
                        onClick={() => {
                          const dateStr = dayjs(date).format("YYYY-MM-DD");
                          const isAvailable = dayAvailability[dateStr];
                          if (isAvailable !== false) {
                            dispatch(
                              updateAppointment({
                                updates: {
                                  date: dayjs(date).format("MM/DD/YYYY"),
                                },
                              })
                            );
                            setSelectedDate(date);
                          }
                        }}
                      >
                        <div
                          className={
                            date?.setHours(0, 0, 0, 0) ===
                            selectedDate?.setHours(0, 0, 0, 0)
                              ? `flex justify-center h-[80px] md:w-full ${buttonSecondary}`
                              : `flex justify-center h-[80px] md:w-full ${buttonPrimary}`
                          }
                          style={{
                            backgroundColor:
                              date?.setHours(0, 0, 0, 0) ===
                              selectedDate?.setHours(0, 0, 0, 0)
                                ? appointment?.shopData?.color
                                : "white",

                            boxShadow:
                              appointment?.shopData?.shadow === "shadow-md"
                                ? `0 4px 6px ${appointment?.shopData?.color}33`
                                : "none",

                            border: `1px solid ${appointment?.shopData?.color}`,

                            fontColor:
                              date?.setHours(0, 0, 0, 0) ===
                              selectedDate?.setHours(0, 0, 0, 0)
                                ? "black"
                                : "white",
                            ...(dayAvailability[
                              dayjs(date).format("YYYY-MM-DD")
                            ] === false && {
                              opacity: 0.5,
                              cursor: "not-allowed",
                            }),
                          }}
                        >
                          {date.toLocaleString("en-US", {
                            weekday: "short",
                          })}
                          <br />
                          {date.getDate()}
                          <br />
                          {date.toLocaleString("en-US", {
                            month: "short",
                          })}
                          <br />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div>
                    <div
                      className={`w-9 ml-2 h-[80px] rounded-none cursor-pointer ${buttonPrimary}`}
                      onClick={loadNextDates}
                      style={{
                        backgroundColor: "white",

                        boxShadow:
                          appointment?.shopData?.shadow === "shadow-md"
                            ? `0 4px 6px ${appointment?.shopData?.color}33`
                            : "none",

                        border: `1px solid ${appointment?.shopData?.color}`,

                        fontColor: "black",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8.25 4.5 7.5 7.5-7.5 7.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {selectedDate && (
          <>
            <br />
            <div
              id="time-picker"
              className={`${cardHeaderClass} flex justify-between`}
              style={{
                backgroundColor: appointment?.shopData?.color,
              }}
            >
              Select a Time Slot (Time slots reflect shop hours)
              <span className="relative flex h-3 w-3 pt-1.5">
                {selectedTime ? (
                  <>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
                  </>
                ) : (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                  </>
                )}
              </span>
            </div>
            <div
              className={cardBodyClass}
              style={{
                borderColor: appointment?.shopData?.color,
              }}
            >
              <div className="grid grid-cols-3 gap-5 md:grid-cols-6">
                {findingAvailableDay || loading ? (
                  <div className="col-span-3 md:col-span-6">
                    <center>
                      <img
                        src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
                        alt="loading"
                      />
                      <br />
                      <br />
                      Finding the best times for you!
                    </center>
                  </div>
                ) : Object.keys(bookingTimes).length === 0 ? (
                  <div className="col-span-3 md:col-span-6">
                    <div className="mx-6">
                      <pre
                        style={{
                          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word",
                          overflow: "auto",
                        }}
                      >
                        {noSlotsMessage}
                      </pre>
                    </div>
                  </div>
                ) : (
                  bookingTimes.map((time) => (
                    <div
                      key={time}
                      service-time={time}
                      className={`${
                        time === selectedTime ? buttonSecondary : buttonPrimary
                      } cursor-pointer`}
                      onClick={() => {
                        dispatch(
                          updateAppointment({
                            updates: { time: time },
                          })
                        );
                        setSelectedTime(time);
                        if (!appointment?.shopData?.requireComments) {
                          setClear(true);
                        }

                        if (appointment?.shopData?.complimentaryRide) {
                          setBounceComplimentaryRide(false);
                          setClear(false);
                        }
                      }}
                      style={{
                        backgroundColor:
                          time === selectedTime
                            ? appointment?.shopData?.color
                            : "white",
                        border: `1px solid ${appointment?.shopData?.color}`,
                        boxShadow:
                          appointment?.shopData?.shadow === "shadow-md"
                            ? `0 4px 6px ${appointment?.shopData?.color}33`
                            : "none",
                      }}
                    >
                      {time}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {selectedTime && appointment?.shopData?.complimentaryRide && (
          <>
            <br />
            <div
              id="ride-picker"
              className={`${cardHeaderClass} flex justify-between`}
              style={{ backgroundColor: appointment?.shopData?.color }}
            >
              Will you need a complimentary ride?
              <span className="relative flex h-3 w-3 pt-1.5">
                {bounceComplimentaryRide ? (
                  <>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
                  </>
                ) : (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                  </>
                )}
              </span>
            </div>
            <div
              className={cardBodyClass}
              style={{ borderColor: appointment?.shopData?.color }}
            >
              <div className="grid grid-cols-2 gap-5 p-2">
                {["Yes", "No"].map((choice) => (
                  <div
                    key={choice}
                    onClick={() => {
                      dispatch(
                        updateAppointment({
                          updates: { complimentaryRide: choice },
                        })
                      );
                      setComplimentaryRide(choice);
                      setBounceComplimentaryRide(true);
                      if (!appointment?.shopData?.requireComments) {
                        setClear(true);
                      }
                    }}
                  >
                    <center>
                      <div
                        className={`size-20 md:w-[160px] md:h-[80px] p-2 ${
                          choice === complimentaryRide
                            ? buttonSecondary
                            : buttonPrimary
                        }`}
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            choice === complimentaryRide
                              ? appointment?.shopData?.color
                              : "white",
                          border: `1px solid ${appointment?.shopData?.color}`,
                          boxShadow:
                            appointment?.shopData?.shadow === "shadow-md"
                              ? `0 4px 6px ${appointment?.shopData?.color}33`
                              : "none",
                        }}
                      >
                        {choice}
                      </div>
                    </center>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {selectedTime &&
          (complimentaryRide || !appointment?.shopData?.complimentaryRide) && (
            <div>
              <br />
              <div
                id="appointment-summary"
                className={cardHeaderClass}
                style={{
                  backgroundColor: appointment?.shopData?.color,
                }}
              >
                Appointment Summary
              </div>
              <div
                className={cardBodyClass}
                style={{
                  borderColor: appointment?.shopData?.color,
                }}
              >
                <div className="pb-4">
                  You've chosen to{" "}
                  {stayOrDropOff.toLowerCase() === "wait"
                    ? "stay with"
                    : "drop off"}{" "}
                  your{" "}
                  <strong>
                    {appointment?.vehicle?.year} {appointment?.vehicle?.make}{" "}
                    {appointment?.vehicle?.model}
                  </strong>{" "}
                  for service related to <strong>{appointment?.service}</strong>
                  .
                  {appointment?.value === "9" &&
                    " Please describe what we need to inspect in the addition comments box below."}
                </div>
                <div className="pb-4">
                  The date and time of your service will be{" "}
                  <strong>
                    {dayjs(selectedDate).format("MM/DD/YYYY")} at {selectedTime}
                  </strong>
                </div>
                <div>
                  <strong>Additional Comments</strong>
                  <span style={{ color: "red" }}>
                    {appointment.shopData?.requireComments && " - required"}
                  </span>
                  <div>
                    <textarea
                      className={textArea}
                      rows="4"
                      placeholder="Enter any additional comments here..."
                      onChange={onCommentChange}
                      style={{
                        color: "black",
                        backgroundColor: "white",
                        borderColor: appointment?.shopData?.color,
                        outlineColor: appointment?.shopData?.color,
                      }}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
      <br />
      <div>
        <NextBackButtons appointment={appointment} clear={clear} />
      </div>
    </>
  );
};

export { AppointmentPicker };
